// src/pages/markets/board/board/GroupTextEditor.tsx
//
// The admin's edit of a trader group's name, headline and description, shown
// in place of that text inside the info panel. Editing where the text is read
// is the point: the field widths and type sizes are the rendered ones, so an
// admin sees the real result while typing rather than guessing at it.
//
// Only three fields. Colour, seed column and picker visibility are set
// elsewhere, and a text edit must not carry them along.

import { useEffect, useRef, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import type { BoardGroupText } from '../state/boardGroupsContext';

export interface GroupTextEditorProps {
    group: string;
    initial: BoardGroupText;
    onSave: (text: BoardGroupText) => Promise<void>;
    onCancel: () => void;
    // The phone sheet: 44 px fields and buttons, text of at least 12 px.
    touch?: boolean;
}

const FIELD_SX = {
    '& .MuiInputBase-input': { fontSize: '12px', py: 0.5 },
    '& .MuiFormLabel-root': { fontSize: '12px' },
};

// 16 px keeps iOS from zooming into a field, and a label that shrinks to
// 0.75 of its size stays at 12.
const TOUCH_FIELD_SX = {
    '& .MuiInputBase-root': { minHeight: 44 },
    '& .MuiInputBase-root:not(.MuiInputBase-multiline) .MuiInputBase-input': {
        fontSize: '16px',
        boxSizing: 'border-box',
        minHeight: 44,
    },
    '& .MuiInputBase-multiline .MuiInputBase-input': { fontSize: '16px' },
    '& .MuiFormLabel-root': { fontSize: '16px' },
};

const TOUCH_BUTTON_SX = { fontSize: '14px', minHeight: 44, minWidth: 44 };

export function GroupTextEditor({
    group,
    initial,
    onSave,
    onCancel,
    touch,
}: GroupTextEditorProps) {
    const fieldSx = touch ? TOUCH_FIELD_SX : FIELD_SX;
    const [label, setLabel] = useState(initial.label);
    const [oneLiner, setOneLiner] = useState(initial.oneLiner);
    const [description, setDescription] = useState(initial.description);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const labelRef = useRef<HTMLInputElement>(null);

    // The name is what an admin opens this to change most often, so it takes
    // focus rather than making them click into it.
    useEffect(() => {
        labelRef.current?.focus();
        labelRef.current?.select();
    }, []);

    // Empty headline and description are legitimate — clearing them means the
    // group has none. An empty name is not: the picker row would be blank.
    const nameMissing = label.trim().length === 0;

    const dirty =
        label !== initial.label ||
        oneLiner !== initial.oneLiner ||
        description !== initial.description;

    const submit = async () => {
        if (nameMissing || saving) return;
        setSaving(true);
        setError(null);
        try {
            await onSave({ label, oneLiner, description });
        } catch (err) {
            // The panel stays open with the typed text intact: a rejected write
            // must not cost the admin what they wrote.
            setError(err instanceof Error ? err.message : 'Could not save.');
            setSaving(false);
        }
    };

    // Escape cancels from any field. Enter submits from the single-line fields
    // only — the description is a textarea, where Enter is a paragraph break.
    const onKeyDown = (e: React.KeyboardEvent, submitOnEnter: boolean) => {
        // The panel is inside a Popper in a portal; without this the key
        // reaches the board underneath.
        e.stopPropagation();
        if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
            return;
        }
        if (submitOnEnter && e.key === 'Enter') {
            e.preventDefault();
            void submit();
        }
    };

    return (
        <Box
            component="form"
            aria-label={`Edit ${group}`}
            onSubmit={e => {
                e.preventDefault();
                void submit();
            }}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                mt: 0.5,
            }}
        >
            <TextField
                inputRef={labelRef}
                label="Name"
                value={label}
                onChange={e => setLabel(e.target.value)}
                onKeyDown={e => onKeyDown(e, true)}
                size="small"
                error={nameMissing}
                helperText={nameMissing ? 'A group needs a name.' : ' '}
                FormHelperTextProps={{ sx: { fontSize: touch ? '12px' : '10px', m: 0 } }}
                sx={fieldSx}
            />
            <TextField
                label="Headline"
                value={oneLiner}
                onChange={e => setOneLiner(e.target.value)}
                onKeyDown={e => onKeyDown(e, true)}
                size="small"
                sx={fieldSx}
            />
            <TextField
                label="Description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                onKeyDown={e => onKeyDown(e, false)}
                size="small"
                multiline
                minRows={3}
                maxRows={10}
                sx={fieldSx}
            />

            {error && (
                <Typography
                    role="alert"
                    sx={{ fontSize: touch ? '12px' : '11px', color: 'error.main' }}
                >
                    {error}
                </Typography>
            )}

            <Box sx={{ display: 'flex', flexWrap: touch ? 'wrap' : 'nowrap', gap: 0.75, alignItems: 'center' }}>
                <Button
                    // Deliberately not type="submit". This renders inside a
                    // HoverPanel, whose Paper calls preventDefault() on every
                    // click that reaches it — and a submit button's form
                    // submission is that click's default action, so it was
                    // cancelled on the way up and the button did nothing while
                    // Enter still worked. An explicit onClick does not depend
                    // on the default action surviving the trip.
                    type="button"
                    onClick={() => void submit()}
                    size="small"
                    variant="contained"
                    disabled={nameMissing || saving || !dirty}
                    sx={touch ? TOUCH_BUTTON_SX : { fontSize: '11px', py: 0.25 }}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
                <Button
                    size="small"
                    onClick={onCancel}
                    disabled={saving}
                    sx={touch ? TOUCH_BUTTON_SX : { fontSize: '11px', py: 0.25 }}
                >
                    Cancel
                </Button>
                <Typography
                    sx={{ fontSize: touch ? '12px' : '10px', color: 'text.disabled', ml: 'auto' }}
                >
                    Everyone on the desk sees this.
                </Typography>
            </Box>
        </Box>
    );
}
